import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import {
  Autocomplete,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { BsChatDots } from 'react-icons/bs';
import { FaTooth } from 'react-icons/fa';
import { IoMdPerson } from 'react-icons/io';
import {
  IoAdd,
  IoChevronBack,
  IoChevronDown,
  IoChevronUp,
  IoInformationCircle,
  IoMailOutline,
  IoMedicalOutline,
  IoMedkitOutline,
  IoSettingsOutline,
  IoShieldCheckmarkOutline,
} from 'react-icons/io5';

import MUISelectInput from '../../../../Components/Inputs/MuiSelectInput/MUISelectInput';
import PhoneInput from '../../../../Components/Inputs/MuiTelInput/MuiTelInput';
import { useAppSelector } from '../../../../hooks/useReduxHook';
import { createBasicInformation } from '../../../../services/identity/createBasicInformation';
import { createCareAdvisorPatient } from '../../../../services/identity/createCareAdvisorPatient';
import { listAllClientTags } from '../../../../services/identity/listAllClientTags';
import { listAllLanguages } from '../../../../services/identity/listAllLanguages';
import {
  ClientTag,
  BasicInfo,
  Language,
  Organization,
  UserRoleName,
} from '../../../../services/identity/types/UserProfile';
import { updateUserClientTag } from '../../../../services/identity/updateUserClientTag';
import { getClientDivisionOptions } from '../../../../services/identity/clientDivisionOptions';
import { displayErrorDetails } from '../../../../Utils/displayError';
import { genderIdentity, genderOptions, pronounsOptions } from '../../../../Utils/selectButtonOptions';
import { stateOptions } from '../../../../Utils/selectOptions';
import addNewUserStyles from '../../Collector/AddNewUser/addNewUser.module.scss';
import styles from './createUserPage.module.scss';

const muiInputSx = {
  '& .MuiInputBase-root': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Poppins, sans-serif',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#000000',
  },
  '& .MuiFormLabel-asterisk': {
    color: '#D32F2F',
  },
};

const muiSelectSx = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: '16px',
  '& .MuiSelect-select': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#000000',
  },
};

const switchSx = {
  '& .Mui-checked': { color: 'var(--primary-color)' },
  '& .Mui-checked + .MuiSwitch-track': {
    backgroundColor: 'var(--primary-color)',
  },
};

const checkboxSx = {
  '&.Mui-checked': {
    color: 'var(--primary-color)',
  },
};

const checkboxLabelSx = {
  '& .MuiFormControlLabel-label': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
  },
};

type TabKey = 'profile' | 'medical' | 'dental' | 'insurance' | 'pharmacy' | 'account';

// DO-3968: the JSX below already renders full content for medical,
// dental, insurance, pharmacy, and account behind `activeTab === '...'`
// gates. The tabs were never registered in `tabItems`, so users could
// only ever reach the Profile tab — making the whole medical-history /
// insurance / pharmacy / account data-entry surface unreachable from
// the "Add new patient" form. Registering them here unlocks the
// existing, already-wired UI.
const tabItems: {
  key: TabKey;
  label: string;
  icon: React.ComponentType;
}[] = [
  { key: 'profile', label: 'Profile', icon: IoMdPerson },
  { key: 'medical', label: 'Medical History', icon: IoMedkitOutline },
  { key: 'dental', label: 'Dental History', icon: FaTooth },
  { key: 'insurance', label: 'Insurance', icon: IoShieldCheckmarkOutline },
  { key: 'pharmacy', label: 'Pharmacy', icon: IoMedicalOutline },
  { key: 'account', label: 'Account', icon: IoSettingsOutline },
];

const medicalConditionLabels: Record<string, string> = {
  arthritis: 'Arthritis',
  cancer: 'Cancer',
  dementia_alzheimers: "Dementia or Alzheimer's disease",
  diabetes: 'Diabetes',
  heart_disease: 'Heart disease',
  dry_mouth: 'Dry mouth (Xerostomia)',
  obesity: 'Obesity',
  pregnancy_or_breastfeeding: 'Pregnancy or breastfeeding',
  respiratory_conditions: 'Respiratory disease',
  stroke: 'Stroke',
};

const substanceOptions = ['Tobacco products', 'Recreational drugs', 'Prescription medication for non-medical reasons'];

const CreateUserPage: React.FC = () => {
  const navigate = useRoleNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const organizations = userInfo?.organizations ?? [];

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('profile');

  // ─── Profile tab state ───
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [gender, setGender] = useState('');
  const [genderIdentityValue, setGenderIdentityValue] = useState('');
  const [pronounsValue, setPronounsValue] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState<Language[]>([]);
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [selectedClientTagId, setSelectedClientTagId] = useState<number | null>(null);
  const [selectedClientDivisionId, setSelectedClientDivisionId] = useState<number | null>(null);
  const divisionOptions = useMemo(
    () =>
      getClientDivisionOptions(allClientTags, selectedClientTagId != null ? [selectedClientTagId] : []).map(
        (option) => ({ id: option.id, value: String(option.value), label: option.label }),
      ),
    [allClientTags, selectedClientTagId],
  );

  // ─── Medical history tab state ───
  const [medicalConditions, setMedicalConditions] = useState<Record<string, boolean>>(
    Object.keys(medicalConditionLabels).reduce((acc, key) => ({ ...acc, [key]: false }), {} as Record<string, boolean>),
  );
  const [noneOfTheAbove, setNoneOfTheAbove] = useState(false);
  const [underCareOfPhysician, setUnderCareOfPhysician] = useState(false);
  const [conditionsTreated, setConditionsTreated] = useState('');
  const [takingMedication, setTakingMedication] = useState(false);
  const [medications, setMedications] = useState('');
  const [allergicToMedications, setAllergicToMedications] = useState(false);
  const [allergies, setAllergies] = useState('');
  const [substances, setSubstances] = useState<string[]>([]);
  const [interestedInQuitting, setInterestedInQuitting] = useState(false);
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');

  // ─── Dental history tab state ───
  const [lastDentalVisit, setLastDentalVisit] = useState('');
  const [whoDidYouSee, setWhoDidYouSee] = useState('');

  // ─── Insurance tab state ───
  const [insCompany, setInsCompany] = useState('');
  const [insMemberId, setInsMemberId] = useState('');
  const [insGroupNumber, setInsGroupNumber] = useState('');
  const [insPhone, setInsPhone] = useState('');
  const [insAddress, setInsAddress] = useState('');
  const [insCity, setInsCity] = useState('');
  const [insState, setInsState] = useState('');
  const [insZip, setInsZip] = useState('');
  const [isPatientPolicyHolder, setIsPatientPolicyHolder] = useState(true);
  const [policyHolderFirstName, setPolicyHolderFirstName] = useState('');
  const [policyHolderLastName, setPolicyHolderLastName] = useState('');
  const [policyHolderDob, setPolicyHolderDob] = useState('');
  const [policyHolderGender, setPolicyHolderGender] = useState('');
  const [policyHolderAddress1, setPolicyHolderAddress1] = useState('');
  const [policyHolderAddress2, setPolicyHolderAddress2] = useState('');
  const [policyHolderCity, setPolicyHolderCity] = useState('');
  const [policyHolderState, setPolicyHolderState] = useState('');
  const [policyHolderZip, setPolicyHolderZip] = useState('');

  // Secondary insurance
  const [showSecondary, setShowSecondary] = useState(false);
  const [secInsCompany, setSecInsCompany] = useState('');
  const [secInsMemberId, setSecInsMemberId] = useState('');
  const [secInsGroupNumber, setSecInsGroupNumber] = useState('');
  const [secInsPhone, setSecInsPhone] = useState('');
  const [secInsAddress, setSecInsAddress] = useState('');
  const [secInsCity, setSecInsCity] = useState('');
  const [secInsState, setSecInsState] = useState('');
  const [secInsZip, setSecInsZip] = useState('');

  // ─── Pharmacy tab state ───
  const [pharmacyName, setPharmacyName] = useState('');
  const [pharmacyAddress, setPharmacyAddress] = useState('');
  const [pharmacyCity, setPharmacyCity] = useState('');
  const [pharmacyState, setPharmacyState] = useState('');
  const [pharmacyZip, setPharmacyZip] = useState('');

  // ─── Invitation state ───
  const [sendInvitation, setSendInvitation] = useState(true);

  // ─── Account settings tab state ───
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(false);

  // ─── Reference data ───
  const [allLanguages, setAllLanguages] = useState<Language[]>([]);

  useEffect(() => {
    listAllLanguages().then(setAllLanguages).catch(displayErrorDetails);
    listAllClientTags('CareAdvisor')
      .then(({ nodes }) => setAllClientTags(nodes))
      .catch(displayErrorDetails);
  }, []);

  useEffect(() => {
    if (organizations.length === 1 && !selectedOrgId) {
      setSelectedOrgId(organizations[0].id);
    }
  }, [organizations, selectedOrgId]);

  const handleMedicalConditionToggle = useCallback(
    (key: string) => {
      setMedicalConditions((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
      if (noneOfTheAbove) {
        setNoneOfTheAbove(false);
      }
    },
    [noneOfTheAbove],
  );

  const handleNoneOfTheAbove = useCallback(() => {
    setNoneOfTheAbove((prev) => !prev);
    if (!noneOfTheAbove) {
      setMedicalConditions(
        Object.keys(medicalConditionLabels).reduce(
          (acc, key) => ({ ...acc, [key]: false }),
          {} as Record<string, boolean>,
        ),
      );
    }
  }, [noneOfTheAbove]);

  const handleSubstanceToggle = useCallback((substance: string) => {
    setSubstances((prev) => (prev.includes(substance) ? prev.filter((s) => s !== substance) : [...prev, substance]));
  }, []);

  const handleSave = useCallback(async () => {
    if (saving) return;

    if (!firstName) {
      toast.error('First name is required.', { className: 'fs-unmask' });
      return;
    }
    if (!lastName) {
      toast.error('Last name is required.', { className: 'fs-unmask' });
      return;
    }
    if (!dateOfBirth) {
      toast.error('Date of birth is required.', { className: 'fs-unmask' });
      return;
    }
    if (sendInvitation && !email) {
      toast.error('Email is required to send an invitation.', { className: 'fs-unmask' });
      return;
    }
    if (allClientTags.length > 0 && !selectedClientTagId) {
      toast.error('Client tag is required.', { className: 'fs-unmask' });
      return;
    }

    setSaving(true);
    try {
      const fullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : undefined;

      const organizationIds = selectedOrgId ? [selectedOrgId] : organizations.map((org: Organization) => org.id);

      const newUser = await createCareAdvisorPatient({
        email: email || undefined,
        phone_number: phoneNumber || undefined,
        type: UserRoleName.Patient,
        organization_ids: organizationIds,
        send_invitation: sendInvitation,
        full_name: sendInvitation ? `${firstName} ${lastName}`.trim() : fullName,
        is_quick_add: !sendInvitation && !email,
        ...(address1 || address2 || city || state || zipCode
          ? {
              address: {
                address1: address1 || undefined,
                address2: address2 || undefined,
                city: city || undefined,
                state: state || undefined,
                zip_code: zipCode || undefined,
              },
            }
          : {}),
      });

      // Create basic info for the new user
      if (newUser?.id) {
        const basicInfoPayload: Record<string, unknown> = {};
        if (firstName) basicInfoPayload.first_name = firstName;
        if (middleName) basicInfoPayload.middle_name = middleName;
        if (lastName) basicInfoPayload.last_name = lastName;
        if (preferredName) basicInfoPayload.preferred_name = preferredName;
        if (dateOfBirth) basicInfoPayload.date_of_birth = dateOfBirth;
        if (gender) basicInfoPayload.gender = gender;
        if (genderIdentityValue) basicInfoPayload.gender_identity = genderIdentityValue;
        if (pronounsValue) basicInfoPayload.pronouns = pronounsValue;
        if (selectedLanguages.length > 0) basicInfoPayload.languages = selectedLanguages.map((l) => Number(l.id));

        if (Object.keys(basicInfoPayload).length > 0) {
          await createBasicInformation(basicInfoPayload as BasicInfo, newUser.id, 'CareAdvisor');
        }

        if (selectedClientTagId) {
          await updateUserClientTag(
            newUser.id,
            {
              client_ids: [selectedClientTagId],
              client_division_ids: selectedClientDivisionId ? [selectedClientDivisionId] : [],
            },
            UserRoleName.CareAdvisor,
          );
        }
      }

      navigate('/users');
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setSaving(false);
    }
  }, [
    saving,
    email,
    firstName,
    middleName,
    lastName,
    preferredName,
    dateOfBirth,
    phoneNumber,
    gender,
    genderIdentityValue,
    pronounsValue,
    selectedLanguages,
    address1,
    address2,
    city,
    state,
    zipCode,
    selectedOrgId,
    allClientTags,
    selectedClientTagId,
    selectedClientDivisionId,
    sendInvitation,
    organizations,
    navigate,
  ]);

  const getOrgServices = (org: Organization) => {
    const services: string[] = [];
    if (org.follow_up_enabled) services.push('Follow-up');
    if (org.care_coordination_enabled) services.push('Care coordination');
    if (org.oral_health_coaching_enabled) services.push('Oral health coaching');
    return services.length > 0 ? services.join(', ') : 'General dental services';
  };

  const renderInsuranceFields = (prefix: 'primary' | 'secondary') => {
    const isPrimary = prefix === 'primary';
    const company = isPrimary ? insCompany : secInsCompany;
    const setCompany = isPrimary ? setInsCompany : setSecInsCompany;
    const memberId = isPrimary ? insMemberId : secInsMemberId;
    const setMemberIdFn = isPrimary ? setInsMemberId : setSecInsMemberId;
    const groupNumber = isPrimary ? insGroupNumber : secInsGroupNumber;
    const setGroupNumberFn = isPrimary ? setInsGroupNumber : setSecInsGroupNumber;
    const phone = isPrimary ? insPhone : secInsPhone;
    const setPhoneFn = isPrimary ? setInsPhone : setSecInsPhone;
    const addr = isPrimary ? insAddress : secInsAddress;
    const setAddrFn = isPrimary ? setInsAddress : setSecInsAddress;
    const cityVal = isPrimary ? insCity : secInsCity;
    const setCityFn = isPrimary ? setInsCity : setSecInsCity;
    const stateVal = isPrimary ? insState : secInsState;
    const setStateFn = isPrimary ? setInsState : setSecInsState;
    const zip = isPrimary ? insZip : secInsZip;
    const setZipFn = isPrimary ? setInsZip : setSecInsZip;

    return (
      <div className={styles.insuranceBody}>
        <TextField
          label="Insurance company"
          required
          size="medium"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          fullWidth
          sx={muiInputSx}
        />

        <div className={styles.fieldRow}>
          <TextField
            label="Member ID"
            required
            size="medium"
            value={memberId}
            onChange={(e) => setMemberIdFn(e.target.value)}
            fullWidth
            sx={muiInputSx}
          />
          <TextField
            label="Group number (Optional)"
            size="medium"
            value={groupNumber}
            onChange={(e) => setGroupNumberFn(e.target.value)}
            fullWidth
            sx={muiInputSx}
          />
        </div>

        <TextField
          label="Insurance phone number"
          required
          size="medium"
          value={phone}
          onChange={(e) => setPhoneFn(e.target.value)}
          fullWidth
          sx={muiInputSx}
        />

        <TextField
          label="Insurance street address"
          required
          size="medium"
          value={addr}
          onChange={(e) => setAddrFn(e.target.value)}
          fullWidth
          sx={muiInputSx}
        />

        <div className={styles.fieldRow}>
          <TextField
            label="Insurance city"
            required
            size="medium"
            value={cityVal}
            onChange={(e) => setCityFn(e.target.value)}
            fullWidth
            sx={muiInputSx}
          />
          <FormControl fullWidth size="medium">
            <InputLabel
              required
              sx={{ fontFamily: 'Poppins, sans-serif', '& .MuiFormLabel-asterisk': { color: '#D32F2F' } }}
            >
              Insurance state
            </InputLabel>
            <Select
              size="medium"
              value={stateVal}
              onChange={(e: SelectChangeEvent) => setStateFn(e.target.value)}
              label="Insurance state"
              required
              fullWidth
              sx={muiSelectSx}
            >
              {stateOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Insurance zip code"
            required
            size="medium"
            value={zip}
            onChange={(e) => setZipFn(e.target.value)}
            fullWidth
            sx={muiInputSx}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <button type="button" className={styles.backButton} onClick={() => navigate('/users')}>
        <IoChevronBack />
        <span>Back</span>
      </button>

      <div className={styles.alert}>
        <IoInformationCircle className={styles.alertIcon} />
        <div className={styles.alertText}>
          <p>
            <strong>Creating patient profile</strong>
          </p>
        </div>
      </div>

      <div className={styles.tabsBar}>
        {tabItems.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ═══ Profile Tab ═══ */}
      {activeTab === 'profile' && (
        <>
          <div className={styles.form}>
            <div className={styles.avatarRow}>
              <div className={styles.avatar}>
                {firstName || lastName ? `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() : '?'}
              </div>
              <button type="button" className={styles.changePictureBtn}>
                Change profile picture
              </button>
            </div>

            <div className={styles.fieldRow}>
              <TextField
                label="First name"
                required
                size="medium"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
              <TextField
                label="Middle name"
                size="medium"
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
              <TextField
                label="Last name"
                required
                size="medium"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
            </div>

            <div className={styles.fieldRow}>
              <TextField
                label="Preferred name (optional)"
                size="medium"
                value={preferredName}
                onChange={(e) => setPreferredName(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
              <TextField
                label="Date of birth"
                required
                size="medium"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={muiInputSx}
              />
            </div>

            <div className={styles.fieldRow}>
              <TextField
                label="Email"
                required
                size="medium"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
              {/*
                DO-3968: the prior plain `TextField` for phone accepted
                any characters at any length (reporter's screenshot
                showed a 23-digit phone number — no mask, no cap). Swap
                to the brand's existing `PhoneInput`, same component
                the Collector's Add New User form already uses. It
                strips non-digits, caps at 10 digits, formats as
                `(XXX) XXX-XXXX`, and stores the value as E.164.
              */}
              <PhoneInput
                name="phone_number"
                label="Phone number"
                required
                value={phoneNumber}
                onChange={(v) => setPhoneNumber(v ?? '')}
              />
            </div>

            <div className={styles.fieldRow}>
              <FormControl fullWidth size="medium">
                <InputLabel
                  required
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    '& .MuiFormLabel-asterisk': { color: '#D32F2F' },
                  }}
                >
                  Gender assigned at birth
                </InputLabel>
                <Select
                  size="medium"
                  value={gender}
                  onChange={(e: SelectChangeEvent) => setGender(e.target.value)}
                  label="Gender assigned at birth"
                  fullWidth
                  sx={muiSelectSx}
                >
                  {genderOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="medium">
                <InputLabel
                  required
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    '& .MuiFormLabel-asterisk': { color: '#D32F2F' },
                  }}
                >
                  Gender identity
                </InputLabel>
                <Select
                  size="medium"
                  value={genderIdentityValue}
                  onChange={(e: SelectChangeEvent) => setGenderIdentityValue(e.target.value)}
                  label="Gender identity"
                  fullWidth
                  sx={muiSelectSx}
                >
                  {genderIdentity.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="medium">
                <InputLabel
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Preferred pronouns (optional)
                </InputLabel>
                <Select
                  size="medium"
                  value={pronounsValue}
                  onChange={(e: SelectChangeEvent) => setPronounsValue(e.target.value)}
                  label="Preferred pronouns (optional)"
                  fullWidth
                  sx={muiSelectSx}
                >
                  {pronounsOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <Autocomplete
              multiple
              size="medium"
              options={allLanguages}
              getOptionLabel={(option) => option.name}
              value={selectedLanguages}
              onChange={(_e, value) => setSelectedLanguages(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Languages" required sx={muiInputSx} />}
              sx={{
                '& .MuiChip-root': {
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                },
              }}
            />

            <div className={styles.fieldRow}>
              <TextField
                label="Address"
                required
                size="medium"
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
              <TextField
                label="Address line 2 (optional)"
                size="medium"
                value={address2}
                onChange={(e) => setAddress2(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
            </div>

            <div className={styles.fieldRow}>
              <TextField
                label="City"
                required
                size="medium"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
              <FormControl fullWidth size="medium">
                <InputLabel
                  required
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    '& .MuiFormLabel-asterisk': { color: '#D32F2F' },
                  }}
                >
                  State
                </InputLabel>
                <Select
                  size="medium"
                  value={state}
                  onChange={(e: SelectChangeEvent) => setState(e.target.value)}
                  label="State"
                  fullWidth
                  sx={muiSelectSx}
                >
                  {stateOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Zip code"
                required
                size="medium"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
            </div>
            {allClientTags.length > 0 && (
              <div>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2 }}>
                  Client tags
                </Typography>
                <p className={addNewUserStyles.clientTagsDescription}>
                  Select the client tag for this patient&apos;s Dentistry.One services.
                </p>
                <div className={addNewUserStyles.inputWrap}>
                  <MUISelectInput
                    name="clientTag"
                    label="Client tag"
                    required
                    value={selectedClientTagId != null ? String(selectedClientTagId) : ''}
                    options={allClientTags.map((tag) => ({
                      id: tag.id,
                      value: String(tag.id),
                      label: tag.name,
                    }))}
                    onChange={(v: string) => {
                      setSelectedClientTagId(v ? Number(v) : null);
                      setSelectedClientDivisionId(null);
                    }}
                  />
                </div>
              </div>
            )}
            {selectedClientTagId != null && divisionOptions.length > 0 && (
              <div>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '20px', mb: 2 }}>
                  Division
                </Typography>
                <div className={addNewUserStyles.inputWrap}>
                  <MUISelectInput
                    name="clientDivision"
                    label="Division"
                    value={selectedClientDivisionId != null ? String(selectedClientDivisionId) : ''}
                    options={divisionOptions}
                    onChange={(v: string) => setSelectedClientDivisionId(v ? Number(v) : null)}
                  />
                </div>
              </div>
            )}
          </div>

          <div className={styles.dependentsSection}>
            <p className={styles.dependentsTitle}>Dependents</p>
            <button type="button" className={styles.addDependentBtn}>
              <IoAdd />
              Add dependent
            </button>
          </div>
        </>
      )}

      {/* ═══ Medical History Tab ═══ */}
      {activeTab === 'medical' && (
        <div className={styles.form}>
          <h2 className={styles.sectionTitle}>
            Do you have, or have you had, any of the following medical conditions?
          </h2>
          <FormGroup className={styles.checkboxGroup}>
            {Object.entries(medicalConditionLabels).map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={medicalConditions[key] || false}
                    onChange={() => handleMedicalConditionToggle(key)}
                    sx={checkboxSx}
                  />
                }
                label={label}
                sx={checkboxLabelSx}
                className={styles.checkboxItem}
              />
            ))}
            <FormControlLabel
              control={<Checkbox checked={noneOfTheAbove} onChange={handleNoneOfTheAbove} sx={checkboxSx} />}
              label="None of the above"
              sx={checkboxLabelSx}
              className={styles.checkboxItem}
            />
          </FormGroup>

          <h2 className={styles.sectionTitle}>
            Are you now, or have you been in the past year, under the care of a physician?
          </h2>
          <div className={styles.switchRow}>
            <span className={styles.switchLabel}>{underCareOfPhysician ? 'Yes' : 'No'}</span>
            <Switch
              checked={underCareOfPhysician}
              onChange={(e) => setUnderCareOfPhysician(e.target.checked)}
              sx={switchSx}
            />
          </div>

          {underCareOfPhysician && (
            <>
              <h2 className={styles.sectionTitle}>What conditions are being treated by your physician?</h2>
              <TextField
                multiline
                rows={3}
                size="medium"
                value={conditionsTreated}
                onChange={(e) => setConditionsTreated(e.target.value)}
                fullWidth
                sx={muiInputSx}
              />
            </>
          )}

          <h2 className={styles.sectionTitle}>Are you taking any medications?</h2>
          <p className={styles.sectionDescription}>
            Within the past month, have you taken (or supposed to be taking) any medications? This includes
            prescription, over the counter, diet supplements, and natural or herbal vitamins.
          </p>
          <div className={styles.switchRow}>
            <span className={styles.switchLabel}>{takingMedication ? 'Yes' : 'No'}</span>
            <Switch checked={takingMedication} onChange={(e) => setTakingMedication(e.target.checked)} sx={switchSx} />
          </div>

          {takingMedication && (
            <TextField
              label="Medications (comma-separated)"
              size="medium"
              value={medications}
              onChange={(e) => setMedications(e.target.value)}
              fullWidth
              sx={muiInputSx}
            />
          )}

          <h2 className={styles.sectionTitle}>
            Are you allergic to any medications, metal, latex, or certain materials?
          </h2>
          <div className={styles.switchRow}>
            <span className={styles.switchLabel}>{allergicToMedications ? 'Yes' : 'No'}</span>
            <Switch
              checked={allergicToMedications}
              onChange={(e) => setAllergicToMedications(e.target.checked)}
              sx={switchSx}
            />
          </div>

          {allergicToMedications && (
            <TextField
              label="Allergies (comma-separated)"
              size="medium"
              value={allergies}
              onChange={(e) => setAllergies(e.target.value)}
              fullWidth
              sx={muiInputSx}
            />
          )}

          <h2 className={styles.sectionTitle}>Do you use any of the following?</h2>
          <FormGroup className={styles.checkboxGroup}>
            {substanceOptions.map((substance) => (
              <FormControlLabel
                key={substance}
                control={
                  <Checkbox
                    checked={substances.includes(substance)}
                    onChange={() => handleSubstanceToggle(substance)}
                    sx={checkboxSx}
                  />
                }
                label={substance}
                sx={checkboxLabelSx}
                className={styles.checkboxItem}
              />
            ))}
          </FormGroup>

          {substances.length > 0 && (
            <>
              <h2 className={styles.sectionTitle}>Are you interested in quitting?</h2>
              <div className={styles.switchRow}>
                <span className={styles.switchLabel}>{interestedInQuitting ? 'Yes' : 'No'}</span>
                <Switch
                  checked={interestedInQuitting}
                  onChange={(e) => setInterestedInQuitting(e.target.checked)}
                  sx={switchSx}
                />
              </div>
            </>
          )}

          <h2 className={styles.sectionTitle}>What is your current height and weight?</h2>
          <p className={styles.sectionDescription}>
            We collect this information to ensure you are getting effective and safe prescriptions.
          </p>
          <div className={styles.fieldRow}>
            <TextField
              label="Height"
              size="medium"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              fullWidth
              sx={muiInputSx}
            />
            <TextField
              label="Weight"
              size="medium"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              fullWidth
              sx={muiInputSx}
            />
          </div>
        </div>
      )}

      {/* ═══ Dental History Tab ═══ */}
      {activeTab === 'dental' && (
        <div className={styles.form}>
          <h2 className={styles.sectionTitle}>When is the last time you saw a dentist in person?</h2>
          <div className={styles.fieldRow}>
            <TextField
              label="Last dental visit"
              size="medium"
              value={lastDentalVisit}
              onChange={(e) => setLastDentalVisit(e.target.value)}
              fullWidth
              sx={muiInputSx}
            />
            <TextField
              label="Who did you see?"
              size="medium"
              value={whoDidYouSee}
              onChange={(e) => setWhoDidYouSee(e.target.value)}
              fullWidth
              sx={muiInputSx}
            />
          </div>
        </div>
      )}

      {/* ═══ Insurance Tab ═══ */}
      {activeTab === 'insurance' && (
        <div className={styles.form}>
          <div className={styles.insuranceSection}>
            <div className={styles.insuranceHeader}>
              <span className={styles.insuranceHeaderTitle}>Primary Insurance information</span>
            </div>
            {renderInsuranceFields('primary')}
          </div>

          {/* Policy holder */}
          <div className={styles.switchRow}>
            <span className={styles.switchLabel}>Is the patient the policy holder?</span>
            <Switch
              checked={isPatientPolicyHolder}
              onChange={(e) => setIsPatientPolicyHolder(e.target.checked)}
              sx={switchSx}
            />
          </div>

          {!isPatientPolicyHolder && (
            <div className={styles.form}>
              <h2 className={styles.sectionTitle}>Policy holder information</h2>
              <div className={styles.fieldRow}>
                <TextField
                  label="First name"
                  required
                  size="medium"
                  value={policyHolderFirstName}
                  onChange={(e) => setPolicyHolderFirstName(e.target.value)}
                  fullWidth
                  sx={muiInputSx}
                />
                <TextField
                  label="Last name"
                  required
                  size="medium"
                  value={policyHolderLastName}
                  onChange={(e) => setPolicyHolderLastName(e.target.value)}
                  fullWidth
                  sx={muiInputSx}
                />
              </div>
              <div className={styles.fieldRow}>
                <TextField
                  label="Date of birth"
                  size="medium"
                  type="date"
                  value={policyHolderDob}
                  onChange={(e) => setPolicyHolderDob(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={muiInputSx}
                />
                <FormControl fullWidth size="medium">
                  <InputLabel
                    required
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      '& .MuiFormLabel-asterisk': { color: '#D32F2F' },
                    }}
                  >
                    Gender assigned at birth
                  </InputLabel>
                  <Select
                    size="medium"
                    value={policyHolderGender}
                    onChange={(e: SelectChangeEvent) => setPolicyHolderGender(e.target.value)}
                    label="Gender assigned at birth"
                    fullWidth
                    sx={muiSelectSx}
                  >
                    {genderOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className={styles.fieldRow}>
                <TextField
                  label="Address"
                  required
                  size="medium"
                  value={policyHolderAddress1}
                  onChange={(e) => setPolicyHolderAddress1(e.target.value)}
                  fullWidth
                  sx={muiInputSx}
                />
                <TextField
                  label="Apartment, suite, etc. (Optional)"
                  size="medium"
                  value={policyHolderAddress2}
                  onChange={(e) => setPolicyHolderAddress2(e.target.value)}
                  fullWidth
                  sx={muiInputSx}
                />
              </div>
              <div className={styles.fieldRow}>
                <TextField
                  label="City"
                  required
                  size="medium"
                  value={policyHolderCity}
                  onChange={(e) => setPolicyHolderCity(e.target.value)}
                  fullWidth
                  sx={muiInputSx}
                />
                <FormControl fullWidth size="medium">
                  <InputLabel
                    required
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      '& .MuiFormLabel-asterisk': { color: '#D32F2F' },
                    }}
                  >
                    State
                  </InputLabel>
                  <Select
                    size="medium"
                    value={policyHolderState}
                    onChange={(e: SelectChangeEvent) => setPolicyHolderState(e.target.value)}
                    label="State"
                    fullWidth
                    sx={muiSelectSx}
                  >
                    {stateOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Zip code"
                  required
                  size="medium"
                  value={policyHolderZip}
                  onChange={(e) => setPolicyHolderZip(e.target.value)}
                  fullWidth
                  sx={muiInputSx}
                />
              </div>
            </div>
          )}

          {/* Secondary insurance */}
          <div className={styles.insuranceSection}>
            <button
              type="button"
              className={styles.insuranceHeaderBtn}
              onClick={() => setShowSecondary(!showSecondary)}
            >
              <div className={styles.insuranceHeaderLeft}>
                {showSecondary ? <IoChevronUp /> : <IoChevronDown />}
                <span className={styles.insuranceHeaderTitle}>Secondary Insurance information</span>
              </div>
            </button>
            {showSecondary && renderInsuranceFields('secondary')}
          </div>
        </div>
      )}

      {/* ═══ Pharmacy Tab ═══ */}
      {activeTab === 'pharmacy' && (
        <div className={styles.form}>
          <h2 className={styles.sectionTitle}>Pharmacy information</h2>
          <TextField
            label="Pharmacy name"
            size="medium"
            value={pharmacyName}
            onChange={(e) => setPharmacyName(e.target.value)}
            fullWidth
            sx={muiInputSx}
          />
          <TextField
            label="Pharmacy street address"
            size="medium"
            value={pharmacyAddress}
            onChange={(e) => setPharmacyAddress(e.target.value)}
            fullWidth
            sx={muiInputSx}
          />
          <div className={styles.fieldRow}>
            <TextField
              label="Pharmacy city"
              size="medium"
              value={pharmacyCity}
              onChange={(e) => setPharmacyCity(e.target.value)}
              fullWidth
              sx={muiInputSx}
            />
            <FormControl fullWidth size="medium">
              <InputLabel
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                Pharmacy state
              </InputLabel>
              <Select
                size="medium"
                value={pharmacyState}
                onChange={(e: SelectChangeEvent) => setPharmacyState(e.target.value)}
                label="Pharmacy state"
                fullWidth
                sx={muiSelectSx}
              >
                {stateOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Pharmacy zip code"
              size="medium"
              value={pharmacyZip}
              onChange={(e) => setPharmacyZip(e.target.value)}
              fullWidth
              sx={muiInputSx}
            />
          </div>
        </div>
      )}

      {/* ═══ Account Settings Tab ═══ */}
      {activeTab === 'account' && (
        <div className={styles.form}>
          <h2 className={styles.sectionTitle}>Account settings</h2>

          <div className={styles.settingsRow}>
            <div className={styles.settingsLabel}>
              <span className={styles.settingsLabelSmall}>Login email</span>
              <span className={styles.settingsValue}>{email || '—'}</span>
            </div>
          </div>

          <h2 className={styles.sectionTitle}>Account security</h2>

          <div className={styles.settingsRow}>
            <div className={styles.settingsRowLeft}>
              <span className={styles.settingsValue}>Multi-factor authentication</span>
            </div>
            <Switch checked={mfaEnabled} onChange={(e) => setMfaEnabled(e.target.checked)} sx={switchSx} />
          </div>

          <h2 className={styles.sectionTitle}>Notification settings</h2>
          <p className={styles.sectionDescription}>
            We&apos;ll send appointment reminders and other messages from our care team to your preferred method of
            communication.
          </p>

          <div className={styles.settingsRow}>
            <div className={styles.settingsRowLeft}>
              <IoMailOutline className={styles.settingsIcon} />
              <span className={styles.settingsValue}>Email</span>
            </div>
            <Switch checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} sx={switchSx} />
          </div>

          <div className={styles.settingsRow}>
            <div className={styles.settingsRowLeft}>
              <BsChatDots className={styles.settingsIcon} />
              <span className={styles.settingsValue}>SMS</span>
            </div>
            <Switch checked={notifySms} onChange={(e) => setNotifySms(e.target.checked)} sx={switchSx} />
          </div>

          {!notifyEmail && !notifySms && (
            <div className={styles.notificationAlert} role="alert">
              <p className={`fs-unmask ${styles.notificationAlertText}`}>
                At least one notification method must be enabled
              </p>
            </div>
          )}
        </div>
      )}

      <div className={styles.sendInvitationSection}>
        <span className={styles.sendInvitationLabel}>Send invitation to user?</span>
        <div className={styles.yesNoToggle}>
          <button
            type="button"
            className={`${styles.yesNoBtn} ${sendInvitation ? styles.yesNoBtnActive : ''}`}
            onClick={() => setSendInvitation(true)}
          >
            Yes
          </button>
          <button
            type="button"
            className={`${styles.yesNoBtn} ${!sendInvitation ? styles.yesNoBtnActive : ''}`}
            onClick={() => setSendInvitation(false)}
          >
            No
          </button>
        </div>
      </div>
      <div className={styles.actions}>
        <button type="button" className={styles.cancelBtn} onClick={() => navigate('/users')}>
          Cancel
        </button>
        <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default CreateUserPage;
